import { useLoaderData } from '@remix-run/react';
import logger from '~/services/logger';
import playNextCloseIcon from './assets/play-next-close-icon.png';
import playNextButton from './assets/play-next-button.png';
import { getPayloadImgSrc } from '~/services/layout/layout.ts';
import type { Feed, FeedEntry } from '~/services/layout/index.ts';
import type { loader } from '~/routes/player.tsx';
import { useState } from 'react';

type Props = {
  playNext: (event: any) => void;
  feed: Feed;
  totalDuration: number;
  position: number;
  isPostroll: boolean;
  showPlayNextOnPostroll: boolean;
  closePlayNextRef: any;
};

export const PlayNext = ({
  playNext,
  feed,
  totalDuration,
  position,
  isPostroll,
  showPlayNextOnPostroll,
  closePlayNextRef,
}: Props) => {
  const { styles } = useLoaderData<typeof loader>();

  const [clicked, setClicked] = useState(false);

  try {
    const entry: FeedEntry = feed?.entry?.[0];

    const { title, summary } = entry || {};

    const playNextImage: string | undefined = getPayloadImgSrc(
      { image_key: styles.play_next_image_key },
      entry
    );

    const handleClick = (event: any) => {
      setClicked(true);
      playNext(event);
    };

    const onClose = () => {
      closePlayNextRef.current = true;
    };

    if (!showPlayNextOnPostroll && isPostroll) return <></>;

    const upNextCountdownSeconds: number = Math.floor(
      Math.abs(totalDuration - position)
    );

    const playNextShowTitle = styles.play_next_show_title;
    const playNextShowSummary = styles.play_next_show_summary;

    if (!playNextImage && !playNextShowTitle && !playNextShowSummary)
      return <></>;

    return (
      <>
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 flex h-full w-full flex-col justify-between">
          <div className="flex flex-row items-center justify-end pb-5 pl-5 pr-5 pt-10 md:pt-5">
            <div className="pr-5 text-xl font-bold text-white">
              {!isPostroll && `Up next in ${upNextCountdownSeconds}`}
            </div>
            <button onClick={onClose} className="pointer-events-auto">
              <img
                src={playNextCloseIcon}
                className="h-5 w-5"
                alt="Play next close icon"
              />
            </button>
          </div>
          <div className="flex w-full justify-end">
            <div className="mb-20 mr-6 w-72">
              <button
                onClick={handleClick}
                disabled={clicked}
                className="pointer-events-auto relative flex flex-col items-center justify-center"
              >
                <div className="relative">
                  {playNextImage && (
                    <>
                      <img
                        alt="Play next"
                        src={playNextButton}
                        className="absolute inset-0 m-auto h-5 w-5"
                      />

                      <img
                        alt="Play next preview"
                        src={playNextImage}
                        className="aspect-video w-full rounded-lg"
                      />
                    </>
                  )}
                </div>
                <div className="flex w-72 flex-col items-start">
                  {playNextShowTitle && title && (
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap text-white">
                      {title}
                    </div>
                  )}
                  {playNextShowSummary && summary && (
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap text-gray-500">
                      {summary}
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  } catch (error: any) {
    logger.info(`PlayNext: ${error.message}`);
    return <></>;
  }
};
